import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AssistantMessageStyles from './AssistantMessage.module.scss';
import { createMarkdownComponents } from './markdownComponents';

/**
 * AssistantMessage - Displays assistant response with markdown rendering
 * 
 * Renders markdown content received from the WebSocket when TEXT_MESSAGE_END event
 * is received. Supports GitHub Flavored Markdown (tables, strikethrough, etc.)
 * 
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the component instance
 * @param {string} props.data - Markdown content (from descriptor)
 * @param {string} props.content - Markdown content (direct prop)
 * @param {boolean} props.isStreaming - Whether content is still being streamed
 * @param {boolean} props.isComplete - Whether the message is finalized
 * @param {boolean} props.error - Whether an error occurred
 * @param {string} props.className - Additional CSS class
 * @param {function} props.onAction - Callback for actions (copy, etc.)
 */
const AssistantMessage = memo(({
  id,
  data,
  content,
  isStreaming,
  isComplete,
  error,
  className,
  onAction
}) => {
  // Support both 'data' (from descriptor) and 'content' (direct prop)
  const markdownContent = data || content || '';

  // Custom components for markdown rendering
  const MarkdownComponents = useMemo(() => createMarkdownComponents(onAction), [onAction]);

  // Handle copy entire message
  const handleCopyMessage = () => {
    navigator.clipboard.writeText(markdownContent);
    onAction?.('copy', { content: markdownContent, type: 'message' });
  };

  return (
    <div
      className={classNames(
        AssistantMessageStyles['message-row'],
        className,
        { [AssistantMessageStyles['error']]: error }
      )}
      data-component-id={id}
      data-streaming={isStreaming}
      data-complete={isComplete}
    >
      <div className={AssistantMessageStyles['chat-message-container']}>
        <div className={AssistantMessageStyles['markdown-content']}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={MarkdownComponents}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>

        {/* Show streaming indicator while content is being received */}
        {isStreaming && (
          <span className={AssistantMessageStyles['streaming-indicator']}>
            ▌
          </span>
        )}
      </div>
    </div>
  );
});

AssistantMessage.displayName = 'AssistantMessage';

// PropTypes for type checking
AssistantMessage.propTypes = {
  id: PropTypes.string,
  /** Markdown content (from descriptor) */
  data: PropTypes.string,
  /** Markdown content (direct prop) */
  content: PropTypes.string,
  isStreaming: PropTypes.bool,
  isComplete: PropTypes.bool,
  error: PropTypes.bool,
  className: PropTypes.string,
  onAction: PropTypes.func
};

AssistantMessage.defaultProps = {
  id: undefined,
  data: '',
  content: '',
  isStreaming: false,
  isComplete: false,
  error: false,
  className: '',
  onAction: undefined
};

// Catalog configuration for registry
AssistantMessage.catalogConfig = {
  type: 'AssistantMessage',
  category: 'message',
  defaultProps: {
    isStreaming: false,
    isComplete: false
  }
};

export default AssistantMessage;
